import React, { useMemo, useState } from 'react'
import { Box, Button, Divider, Grid } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import TaskCreateModal from '../Components/Task/TaskComponents/TaskActions/TaskCreateModal'
import TaskTable from '../Components/Task/TaskComponents/TaskTable'
import SearchInput from '../Components/Common/SearchInput'
import { backgroundColors } from '../Components/Common/Color/Color'
import { useAppSelector } from '../redux/store'
import { useFetchTasks } from '../hooks/taskHooks/useFetchTasks'
import TaskFilter from '../Components/Task/TaskComponents/TaskFilter'
import { selectTask } from '../redux/task/selector'
import { processManageTasksData } from '../utils/taskUtils'
import { useFetchTeamMembers } from '../hooks/teamMemberHooks/useFetchTeamMembers'

const ManageTaskPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0)

  const [filters, setFilters] = useState<{
    Importance: string[]
    OperationalManager: string[]
    FunctionalArea: string[]
    Supervisor: string[]
  }>({
    Importance: [],
    OperationalManager: [],
    FunctionalArea: [],
    Supervisor: [],
  })

  // Use hook for fetching all tasks with refresh trigger
  const { tasks: hookTasks } = useFetchTasks({}, refreshTrigger)
  
  // Get all tasks from Redux store for real-time updates
  const tasksFromStore = useAppSelector(selectTask)
  
  // Use store data if available, otherwise use hook data
  const tasks = tasksFromStore.length > 0 ? tasksFromStore : hookTasks

  // Fetch team members for filter options
  const { teamMembers } = useFetchTeamMembers()

  const handleFilterChange = (selectedFilters: Record<string, string[] | undefined>) => {
    setFilters({
      Importance: selectedFilters.Importance ?? [],
      OperationalManager: selectedFilters.OperationalManager ?? [],
      FunctionalArea: selectedFilters.FunctionalArea ?? [],
      Supervisor: selectedFilters.Supervisor ?? [],
    })
  }

  const searchData = useMemo(() => {
    return processManageTasksData(tasks, filters, searchQuery)
  }, [tasks, filters, searchQuery])

  const handleRefreshData = () => {
    setRefreshTrigger((prev) => prev + 1)
  }

  const handleSearchChange = (query: string) => {
    setSearchQuery(query)
  }

  const handleClearSearch = () => {
    setSearchQuery('')
  }

  return (
    <Box
      component='div'
      sx={{ p: 0, backgroundColor: backgroundColors.white, width: 'auto' }}
      borderRadius={2}
      boxShadow={1}
      data-testid="manage-task-page"
    >
      <Grid
        container
        sx={{ p: 2 }}
        flexDirection='row'
        alignItems={'center'}
        justifyContent='space-between'
      >
        <Grid>
          <TaskCreateModal data-testid="manage-task-create-button" />
        </Grid>
        <Grid>
          <Grid container spacing={2} alignItems='center' display='flex'>
            <Grid>
              <Button
                id='refresh-button'
                color='success'
                startIcon={<RefreshIcon />}
                onClick={handleRefreshData}
                size='small'
                data-testid="manage-task-refresh-button"
              >
                Refresh
              </Button>
            </Grid>
            <Grid>
              <TaskFilter onFilterChange={handleFilterChange} pageName='manageTaskPage' teamMembers={teamMembers} data-testid="manage-task-filter" />
            </Grid>
            <Grid>
              <SearchInput
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                onClearSearch={handleClearSearch}
                data-testid="manage-task-search"
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Divider />
      <TaskTable data={searchData} refreshTable={Boolean(searchQuery)} />
    </Box>
  )
}

export default ManageTaskPage
